import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

const SETTINGS = {
  minFlowers: 5,
  maxFlowers: 9,
  minSize: 9,
  maxSize: 18,
  minFall: 140,
  maxFall: 240,
  minLife: 1100,
  maxLife: 1750,
  spreadX: 55,
  stagger: 18,
} as const;

const PALETTES = [
  ['#ff8fb1', '#ffd1de', '#e7a520'],
  ['#f06b8d', '#ffb0c2', '#d99b18'],
  ['#ffad45', '#ffd47d', '#9b6b19'],
  ['#f3c84b', '#ffe998', '#a56f12'],
  ['#ffffff', '#f4dfe7', '#e0a522'],
  ['#d987d8', '#efb6ef', '#d29a1d'],
  ['#ff725c', '#ffb29f', '#d49a1e'],
] as const;

@Component({
  selector: 'gl-flower-click-effect',
  template: '<div #layer class="gl-flower-click-layer" aria-hidden="true"></div>',
  encapsulation: ViewEncapsulation.None,
  styles: `
    gl-flower-click-effect {
      position: fixed;
      inset: 0;
      z-index: 2147483647;
      display: block;
      overflow: hidden;
      pointer-events: none;
    }
    .gl-flower-click-layer {
      position: absolute;
      inset: 0;
      overflow: hidden;
      pointer-events: none;
    }
    .gl-tiny-flower {
      position: absolute;
      left: 0;
      top: 0;
      width: var(--flower-size);
      height: var(--flower-size);
      opacity: 0;
      pointer-events: none;
      will-change: transform, opacity;
      backface-visibility: hidden;
      animation: gl-tiny-flower-fall var(--flower-life) linear var(--flower-delay) both;
    }
    .gl-tiny-flower svg {
      display: block;
      width: 100%;
      height: 100%;
      overflow: visible;
    }
    @keyframes gl-tiny-flower-fall {
      0% {
        opacity: 0;
        transform: translate3d(-50%, -50%, 0) rotate(var(--rotation-start)) scale(0.72);
      }
      10% {
        opacity: 1;
      }
      32% {
        opacity: 1;
        transform: translate3d(calc(-50% + var(--x-one)), calc(-50% + var(--fall-one)), 0)
          rotate(var(--rotation-one)) scale(1);
      }
      68% {
        opacity: 1;
        transform: translate3d(calc(-50% + var(--x-two)), calc(-50% + var(--fall-two)), 0)
          rotate(var(--rotation-two)) scale(1);
      }
      84% {
        opacity: 1;
      }
      100% {
        opacity: 0;
        transform: translate3d(calc(-50% + var(--x-end)), calc(-50% + var(--fall-end)), 0)
          rotate(var(--rotation-end)) scale(0.88);
      }
    }
  `,
})
export class FlowerClickEffectComponent implements AfterViewInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly removalTimers = new Set<ReturnType<typeof setTimeout>>();
  private listening = false;

  @ViewChild('layer', { static: true }) private readonly layer?: ElementRef<HTMLDivElement>;

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.document.addEventListener('click', this.onDocumentClick, {
      capture: true,
      passive: true,
    });
    this.listening = true;
  }

  ngOnDestroy(): void {
    if (this.listening) {
      this.document.removeEventListener('click', this.onDocumentClick, true);
    }

    for (const timer of this.removalTimers) clearTimeout(timer);
    this.removalTimers.clear();
    const layer = this.layer?.nativeElement;
    if (typeof layer?.replaceChildren === 'function') {
      layer.replaceChildren();
    }
  }

  private readonly onDocumentClick = (event: MouseEvent): void => {
    this.burst(event.clientX, event.clientY);
  };

  private burst(x: number, y: number): void {
    const layer = this.layer?.nativeElement;
    if (!layer) return;

    const count = Math.floor(this.random(SETTINGS.minFlowers, SETTINGS.maxFlowers + 1));
    for (let index = 0; index < count; index += 1) {
      this.createFlower(layer, x, y, index, count);
    }
  }

  private createFlower(
    layer: HTMLDivElement,
    x: number,
    y: number,
    index: number,
    total: number,
  ): void {
    const flower = this.document.createElement('div');
    const size = this.random(SETTINGS.minSize, SETTINGS.maxSize);
    const life = this.random(SETTINGS.minLife, SETTINGS.maxLife);
    const normalized = total <= 1 ? 0 : (index / (total - 1)) * 2 - 1;
    const endX = normalized * SETTINGS.spreadX + this.random(-28, 28);
    const sway = this.random(5, 24);
    const fall = this.random(SETTINGS.minFall, SETTINGS.maxFall);
    const startRotation = this.random(0, 360);
    const rotation = this.random(-260, 260);
    const delay = index * SETTINGS.stagger;

    flower.className = 'gl-tiny-flower';
    flower.setAttribute('aria-hidden', 'true');
    flower.style.left = `${x}px`;
    flower.style.top = `${y}px`;
    flower.style.setProperty('--flower-size', `${size}px`);
    flower.style.setProperty('--flower-life', `${life}ms`);
    flower.style.setProperty('--flower-delay', `${delay}ms`);
    flower.style.setProperty('--x-one', `${endX * 0.38 + sway}px`);
    flower.style.setProperty('--x-two', `${endX * 0.74 - sway}px`);
    flower.style.setProperty('--x-end', `${endX}px`);
    flower.style.setProperty('--fall-one', `${fall * 0.22}px`);
    flower.style.setProperty('--fall-two', `${fall * 0.54}px`);
    flower.style.setProperty('--fall-end', `${fall}px`);
    flower.style.setProperty('--rotation-start', `${startRotation}deg`);
    flower.style.setProperty('--rotation-one', `${startRotation + rotation * 0.28}deg`);
    flower.style.setProperty('--rotation-two', `${startRotation + rotation * 0.68}deg`);
    flower.style.setProperty('--rotation-end', `${startRotation + rotation}deg`);
    flower.innerHTML = this.flowerSvg(
      PALETTES[Math.floor(Math.random() * PALETTES.length)],
      Math.floor(Math.random() * 3),
    );

    let removalTimer: ReturnType<typeof setTimeout> | undefined;
    const remove = (): void => {
      flower.remove();
      if (removalTimer) {
        clearTimeout(removalTimer);
        this.removalTimers.delete(removalTimer);
      }
    };

    flower.addEventListener('animationend', remove, { once: true });
    layer.appendChild(flower);
    removalTimer = setTimeout(remove, life + delay + 100);
    this.removalTimers.add(removalTimer);
  }

  private flowerSvg(palette: (typeof PALETTES)[number], variant: number): string {
    const [main, light, center] = palette;
    const petals = variant === 0 ? 5 : variant === 1 ? 6 : 8;
    const radiusX = variant === 2 ? 8 : 11;
    const radiusY = variant === 2 ? 22 : 20;
    let petalMarkup = '';

    for (let index = 0; index < petals; index += 1) {
      const angle = (index * 360) / petals;
      petalMarkup += `<ellipse cx="50" cy="27" rx="${radiusX}" ry="${radiusY}" fill="${index % 2 ? light : main}" transform="rotate(${angle} 50 50)"/>`;
    }

    return `<svg viewBox="0 0 100 100" aria-hidden="true">${petalMarkup}<circle cx="50" cy="50" r="${variant === 2 ? 10 : 12}" fill="${center}"/><circle cx="46" cy="46" r="3" fill="rgba(255,255,255,.55)"/></svg>`;
  }

  private random(minimum: number, maximum: number): number {
    return Math.random() * (maximum - minimum) + minimum;
  }
}
